/**
 * ============================================================================
 * useBlackoutDates - PRODUCTION-READY BLACKOUT DATES HOOKS
 * ============================================================================
 *
 * TanStack Query hooks for Blackout Dates management with:
 * - Smart caching (blackout dates cached for 10min)
 * - CRUD mutations with auto-invalidation
 * - Proper error handling
 *
 * ============================================================================
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import vendorApi from '../services/api';

// ============================================================================
// TYPES
// ============================================================================

export interface BlackoutDate {
  id?: number;
  date: string;
  end_date?: string;
  reason: string;
  sport_id?: number | null;
  applies_to?: string;
}

export interface CreateBlackoutDateData {
  venueId: number;
  date: string;
  endDate?: string;
  reason: string;
  sportId?: number | null;
}

export interface DeleteBlackoutDateData {
  venueId: number;
  blackoutId: number;
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new blackout date
 */
export function useCreateBlackoutDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBlackoutDateData) => {
      // Use the existing addBlackoutDate method
      return await vendorApi.addBlackoutDate(
        data.venueId,
        data.date,
        data.reason,
        data.sportId
      );
    },

    onSuccess: (data, variables) => {
      // Invalidate venue-info query to refresh blackout dates
      queryClient.invalidateQueries({ queryKey: ['venue-info'] });
    },

    onError: (error: any) => {
      console.error('Failed to create blackout date:', error);
      throw error;
    },
  });
}

/**
 * Delete a blackout date
 */
export function useDeleteBlackoutDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, blackoutId }: DeleteBlackoutDateData) => {
      return await vendorApi.removeBlackoutDate(venueId, blackoutId);
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['venue-info'] });
    },

    onError: (error: any) => {
      console.error('Failed to delete blackout date:', error);
      throw error;
    },
  });
}
